class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.drawer = this.querySelector('#CartDrawer');
    this.onDocumentClick = this.handleDocumentClick.bind(this);
    this.onKeyUp = this.handleKeyUp.bind(this);
    this.currentItemCount = this.getItemCountFromDom();

    this.onRebuyAdd = this.handleRebuyAdd.bind(this);

    this.addEventListener('click', this.handleClick.bind(this));
    this.addEventListener(
      'change',
      debounce((event) => this.handleChange(event), 300)
    );
    this.addEventListener('toggle', this.handleGiftToggle.bind(this), true);
    this.addEventListener('input', this.handleNoteInput.bind(this));
  }

  connectedCallback() {
    // Document delegation survives sticky-header / icon innerHTML updates
    document.addEventListener('click', this.onDocumentClick);
    document.addEventListener('rebuy.add', this.onRebuyAdd);
    this.revealWhenStylesReady();
  }

  revealWhenStylesReady() {
    if (!this.hasAttribute('hidden')) return;

    const stylesheet = document.querySelector('link[href*="component-cart-drawer.css"]');
    if (!stylesheet || stylesheet.media === 'all') {
      this.removeAttribute('hidden');
      return;
    }

    stylesheet.addEventListener(
      'load',
      () => {
        this.removeAttribute('hidden');
      },
      { once: true }
    );
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onDocumentClick);
    document.removeEventListener('keyup', this.onKeyUp);
    document.removeEventListener('rebuy.add', this.onRebuyAdd);
  }

  handleKeyUp(event) {
    if (event.code === 'Escape') this.close();
  }

  getItemCountFromDom() {
    const inputs = this.querySelectorAll('.quantity__input[name="updates[]"]');
    return Array.from(inputs).reduce((total, input) => total + (parseInt(input.value, 10) || 0), 0);
  }

  handleDocumentClick(event) {
    const icon = event.target.closest('#cart-icon-bubble');
    if (!icon) return;

    // Cart page has no drawer — leave the link alone
    if (!document.querySelector('cart-drawer')) return;

    event.preventDefault();

    if (this.classList.contains('active')) {
      this.close();
    } else {
      this.open(icon);
    }
  }

  handleClick(event) {
    if (event.target.id === 'CartDrawer-Overlay') {
      this.close();
      return;
    }

    const closeButton = event.target.closest('[data-cart-drawer-close]');
    if (closeButton) {
      event.preventDefault();
      this.close();
      return;
    }

    const removeButton = event.target.closest('.cart-drawer__remove');
    if (removeButton) {
      event.preventDefault();
      this.updateQuantity(removeButton.dataset.index, 0);
    }
  }

  handleGiftToggle(event) {
    if (event.target.id !== 'CartDrawer-gift') return;

    const isGift = event.target.open || event.target.classList.contains('has-message');
    const body = JSON.stringify({
      attributes: { 'This Order Is A Gift': isGift ? 'Yes' : 'No' },
    });
    fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } });
  }

  handleNoteInput(event) {
    if (event.target.id !== 'CartDrawer-note') return;

    const gift = this.querySelector('#CartDrawer-gift');
    if (gift) gift.classList.toggle('has-message', event.target.value.trim() !== '');
  }

  handleChange(event) {
    if (event.target.id === 'CartDrawer-note') {
      const body = JSON.stringify({ note: event.target.value });
      fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } });
      return;
    }

    if (event.target.name !== 'updates[]' || !event.target.dataset.index) return;

    this.updateQuantity(
      event.target.dataset.index,
      event.target.value,
      document.activeElement.getAttribute('name')
    );
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    if (this.classList.contains('active')) return;

    this.querySelectorAll('[data-fs-bar]').forEach((bar) => bar.style.removeProperty('--fs-from'));
    this.removeAttribute('hidden');
    this.classList.add('active');
    document.body.classList.add('cart-drawer-open');
    document.addEventListener('keyup', this.onKeyUp);

    this.drawer = this.querySelector('#CartDrawer') || this.drawer;

    this.drawer.addEventListener(
      'transitionend',
      () => {
        this.drawer.focus();
        trapFocus(this.drawer);
      },
      { once: true }
    );
  }

  close() {
    this.classList.remove('active');
    document.body.classList.remove('cart-drawer-open');
    document.removeEventListener('keyup', this.onKeyUp);
    removeTrapFocus(this.activeElement);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  renderContents(parsedState) {
    this.replaceSections(parsedState);
    this.classList.toggle('is-empty', this.isEmptyCart(parsedState));
    this.currentItemCount = parsedState.item_count ?? this.getItemCountFromDom();
    this.open();
  }

  isEmptyCart(parsedState) {
    if (typeof parsedState.item_count === 'number') {
      return parsedState.item_count === 0;
    }
    return this.querySelector('.cart-drawer__items') === null;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '#CartDrawer',
      },
    ];
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    const el = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    return el ? el.innerHTML : '';
  }

  replaceSections(parsedState) {
    // Grow the free shipping meter from its previous width instead of from empty
    const previousBarWidths = {};
    this.querySelectorAll('[data-fs-bar]').forEach((bar) => {
      previousBarWidths[bar.dataset.fsBar] = bar.style.width;
    });

    this.getSectionsToRender().forEach((section) => {
      const sectionHtml = parsedState.sections && parsedState.sections[section.section];
      if (!sectionHtml) return;

      const elementToReplace =
        document.getElementById(section.id)?.querySelector(section.selector) ||
        document.getElementById(section.id);
      if (!elementToReplace) return;

      const newContent = this.getSectionInnerHTML(sectionHtml, section.selector);
      if (!newContent) return;

      elementToReplace.innerHTML = newContent;
    });

    this.querySelectorAll('[data-fs-bar]').forEach((bar) => {
      const previousWidth = previousBarWidths[bar.dataset.fsBar];
      if (previousWidth) bar.style.setProperty('--fs-from', previousWidth);
    });

    this.drawer = this.querySelector('#CartDrawer');
    this.initRebuyWidget();
  }

  initRebuyWidget() {
    if (window.Rebuy && typeof window.Rebuy.init === 'function') {
      window.Rebuy.init();
    }
  }

  handleRebuyAdd() {
    const body = JSON.stringify({
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname,
    });

    fetch(`${routes.cart_update_url}`, { ...fetchConfig(), body })
      .then((response) => response.json())
      .then((parsedState) => {
        this.replaceSections(parsedState);
        this.classList.toggle('is-empty', this.isEmptyCart(parsedState));
        this.currentItemCount = parsedState.item_count ?? this.getItemCountFromDom();
      })
      .catch(() => {});
  }

  updateQuantity(line, quantity, name) {
    this.enableLoading(line);

    const body = JSON.stringify({
      line,
      quantity,
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname,
    });

    fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => response.text())
      .then((state) => {
        const parsedState = JSON.parse(state);
        this.replaceSections(parsedState);
        this.classList.toggle('is-empty', this.isEmptyCart(parsedState));
        this.updateLineItemErrors(line, quantity, parsedState.item_count);

        const lineItem = document.getElementById(`CartDrawer-Item-${line}`);
        if (lineItem && name) {
          const focusTarget = lineItem.querySelector(`[name="${name}"]`);
          if (focusTarget) focusTarget.focus();
        }

        this.disableLoading();
      })
      .catch(() => {
        const errors = document.getElementById('CartDrawer-Errors');
        if (errors) errors.textContent = window.cartStrings.error;
        this.disableLoading();
      });
  }

  updateLineItemErrors(line, quantity, itemCount) {
    const requestedQuantity = parseInt(quantity, 10);
    const showError =
      this.currentItemCount === itemCount &&
      requestedQuantity !== 0 &&
      !Number.isNaN(requestedQuantity);

    // Clear prior errors after DOM replace, then set if needed
    this.querySelectorAll('.cart-drawer__item-error').forEach((el) => {
      el.hidden = true;
      const text = el.querySelector('.cart-drawer__item-error-text');
      if (text) text.textContent = '';
    });

    if (showError) {
      const errorEl = document.getElementById(`CartDrawer-LineItemError-${line}`);
      const quantityInput = document.getElementById(`Drawer-Quantity-${line}`);
      if (errorEl && quantityInput && window.cartStrings?.quantityError) {
        const text = errorEl.querySelector('.cart-drawer__item-error-text');
        if (text) {
          text.innerHTML = window.cartStrings.quantityError.replace(
            '[quantity]',
            quantityInput.value
          );
        }
        errorEl.hidden = false;
      }
    }

    this.currentItemCount = itemCount;
  }

  enableLoading(line) {
    const item = document.getElementById(`CartDrawer-Item-${line}`);
    if (item) item.classList.add('cart-drawer__item--loading');
    this.classList.add('cart-drawer--loading');
  }

  disableLoading() {
    this.classList.remove('cart-drawer--loading');
    this.querySelectorAll('.cart-drawer__item--loading').forEach((item) => {
      item.classList.remove('cart-drawer__item--loading');
    });
  }
}

customElements.define('cart-drawer', CartDrawer);
